import type { EditionCapabilities, MeResponse } from '@dbrain/shared';
import { Icons, type IconComponent } from '@dbrain/ui';

/**
 * Yönetim (06 §23.1): the organization's one admin area, a row of tabs
 * across its pages. Organization admins see every tab but Lisans, which is
 * the super admins' (D-126); a domain admin (Phase 13d) only Erişim ve
 * yetkiler, Kullanım ve limitler, Denetim kaydı and Skill analitiği, with
 * their domains' data (D-101), and since Phase 15e Entegrasyonlar, where they
 * see only Microsoft 365's switch for their own teams (D-101 amended; the
 * page and the API keep the rest from them). Members see a plain refusal;
 * the API answers them 403 as well.
 */
export type AdminSection = 'access' | 'models' | 'integrations' | 'usage' | 'policies' | 'audit' | 'skills' | 'identity' | 'license';

/** `superAdmin`: the firm's super admins (and the break-glass admin) only, not its other admins (D-126) */
export const SECTIONS: { id: AdminSection; icon: IconComponent; domainAdmin?: boolean; superAdmin?: boolean; capability?: keyof EditionCapabilities }[] = [
  { id: 'access', icon: Icons.access, domainAdmin: true },
  { id: 'models', icon: Icons.models },
  { id: 'integrations', icon: Icons.integrations, domainAdmin: true },
  { id: 'usage', icon: Icons.usage, domainAdmin: true },
  { id: 'policies', icon: Icons.policies },
  { id: 'audit', icon: Icons.audit, domainAdmin: true },
  { id: 'skills', icon: Icons.skills, domainAdmin: true },
  { id: 'identity', icon: Icons.lock, capability: 'directoryLogin' },
  { id: 'license', icon: Icons.key, superAdmin: true, capability: 'licensing' },
];

export const isOrganizationAdmin = (me: MeResponse) => me.tenant.role === 'owner' || me.tenant.role === 'admin';
export const isDomainAdmin = (me: MeResponse) => Boolean(me.domains?.some((d) => d.admin));

/**
 * The tabs this person may open, in the row's order. Yönetim exists only where the edition has organizations (its
 * routes need the capability): a bireysel person owns their own account but opens none of it (16c.3: Skill'ler no
 * longer links them to Skill analitiği).
 */
export function adminSections(me: MeResponse, capabilities: EditionCapabilities): AdminSection[] {
  if (!capabilities.organization) return [];
  const organization = isOrganizationAdmin(me);
  const domain = isDomainAdmin(me);
  const allowed = (s: (typeof SECTIONS)[number]) => (s.superAdmin ? me.tenant.role === 'owner' : organization || (domain && s.domainAdmin));
  return SECTIONS.filter((s) => allowed(s) && (!s.capability || capabilities[s.capability])).map((s) => s.id);
}
